import React, { useEffect, useState, useCallback } from 'react';
import { PartyPopper, Twitter, MessageCircle, Copy, X, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../utils/authUtils';

/**
 * Fires a celebration toast for each approved upload the scribe hasn't
 * acknowledged yet. One-tap share to Twitter/X, WhatsApp, or native Web
 * Share API, plus "Copy link".
 *
 * Polls /api/writer/approval-notifications on mount + every 60s while the
 * Scribe Dashboard is open.
 */
const ApprovalNotificationsToaster = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [shown, setShown] = useState(new Set());

  const buildShareUrl = (item) => {
    const base = window.location.origin;
    if (item.kind === 'series') {
      return `${base}/library?series=${encodeURIComponent(item.id)}`;
    }
    if (item.type === 'ebook' || item.type === 'text') {
      return `${base}/reader/${item.id}`;
    }
    return `${base}/player/${item.id}`;
  };

  const shareText = (item) =>
    `Just published "${item.title}" on Smarter Learning Hub — check it out!`;

  const ack = useCallback(
    async (id) => {
      try {
        await authFetch(
          `${BACKEND_URL}/api/writer/approval-notifications/${id}/ack`,
          { method: 'POST' }
        );
      } catch {
        /* best-effort */
      }
    },
    [BACKEND_URL]
  );

  const fire = useCallback(
    (item) => {
      const url = buildShareUrl(item);
      const text = shareText(item);

      const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(url)}`;
      const whatsappHref = `https://wa.me/?text=${encodeURIComponent(
        `${text} ${url}`
      )}`;

      const handleCopy = async () => {
        try {
          await navigator.clipboard.writeText(url);
          toast.success('Link copied!');
        } catch {
          toast.error("Couldn't copy link");
        }
      };

      const handleNativeShare = async () => {
        if (navigator.share) {
          try {
            await navigator.share({ title: item.title, text, url });
          } catch {
            /* user cancelled */
          }
        } else {
          handleCopy();
        }
      };

      toast.custom(
        (t) => (
          <div
            className={`w-[92vw] max-w-md pointer-events-auto rounded-2xl border border-emerald-400/30 shadow-2xl bg-slate-900 overflow-hidden ${
              t.visible ? 'animate-[enter_200ms_ease-out]' : 'opacity-0'
            }`}
            data-testid={`approval-toast-${item.id}`}
          >
            <div className="px-4 pt-4 pb-3 bg-gradient-to-br from-emerald-500/15 via-teal-500/10 to-transparent flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 shrink-0 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <PartyPopper className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">
                  Upload approved
                </p>
                <p className="mt-0.5 text-sm font-semibold text-white line-clamp-2 leading-snug">
                  "{item.title}" is live
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  Share it with your audience in one tap.
                </p>
              </div>
              <button
                aria-label="Dismiss"
                onClick={() => {
                  ack(item.id);
                  toast.dismiss(t.id);
                }}
                className="text-slate-400 hover:text-white transition-colors shrink-0"
                data-testid="approval-toast-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-4 py-3 bg-slate-900 flex flex-wrap gap-2">
              <button
                onClick={() => {
                  window.open(twitterHref, '_blank', 'noopener');
                  ack(item.id);
                  toast.dismiss(t.id);
                }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#1DA1F2] hover:bg-[#1a8cd8] px-3 py-1.5 rounded-lg transition-colors"
                data-testid="approval-toast-twitter"
              >
                <Twitter className="w-3.5 h-3.5" />
                Twitter / X
              </button>
              <button
                onClick={() => {
                  window.open(whatsappHref, '_blank', 'noopener');
                  ack(item.id);
                  toast.dismiss(t.id);
                }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#25D366] hover:bg-[#1fb759] px-3 py-1.5 rounded-lg transition-colors"
                data-testid="approval-toast-whatsapp"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                WhatsApp
              </button>
              <button
                onClick={handleNativeShare}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-slate-700 hover:bg-slate-600 px-3 py-1.5 rounded-lg transition-colors"
                data-testid="approval-toast-share"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                More
              </button>
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-200 hover:text-white bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded-lg border border-slate-700 transition-colors"
                data-testid="approval-toast-copy"
              >
                <Copy className="w-3.5 h-3.5" />
                Copy link
              </button>
            </div>
          </div>
        ),
        { duration: 15000, position: 'top-right' }
      );
    },
    [ack]
  );

  const poll = useCallback(async () => {
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/writer/approval-notifications`
      );
      if (!r.ok) return;
      const { notifications } = await r.json();
      (notifications || []).forEach((n) => {
        if (shown.has(n.id)) return;
        shown.add(n.id);
        fire(n);
      });
      if (notifications?.length) setShown(new Set(shown));
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BACKEND_URL, fire]);

  useEffect(() => {
    poll();
    const interval = setInterval(poll, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default ApprovalNotificationsToaster;
